import { Badge } from '@tempo-io/sui-badge';
import { Warning } from '@tempo-io/sui-icons';
import { Skeleton } from '@tempo-io/sui-skeleton';
import { useTranslation } from 'react-i18next';
import { getIsDeadlineAtRisk } from '@/components/loop-planner/common/lib/getIsAtRisk';
import { TooltipScheduleBar } from '@/components/loop-planner/common/TooltipScheduleBar';
import { useScheduleBarData } from '@/components/modular-item-panel/hooks/useScheduleBarData';
import { DateFormat, formatLocalDateWithFallback } from '@/lib/utils/common/dateFormats';
import type { TimelineScenarioContext } from './useTimelineTableColumns';

const formatDate = (dateString: string | null | undefined, fallback: string) =>
  formatLocalDateWithFallback({
    dateString,
    formatStr: DateFormat.MONTH_SHORT_DAY_SHORT_YEAR,
    fallback,
  });

const DateFieldSkeleton = ({ alignEnd }: { alignEnd?: boolean }) => (
  <div className={`flex flex-col gap-1 ${alignEnd ? 'items-end' : ''}`}>
    <Skeleton className="h-4 w-24" />
    <Skeleton className="h-4 w-20" />
  </div>
);

export const TimelineItemTooltipScheduleBar = ({
  itemId,
  scenarioContext,
}: {
  itemId: number;
  scenarioContext: TimelineScenarioContext;
}) => {
  const { t } = useTranslation();
  const { forecastedStart, forecastedFinish, latestFinish, deadline, isLoading } =
    useScheduleBarData(itemId, scenarioContext);

  const fallback = t(($) => $.common.notAvailable);
  const isAtRisk = getIsDeadlineAtRisk({ forecastedFinish, deadline });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 px-4 py-3">
        <Skeleton className="h-7 w-full rounded-lg" />
        <div className="flex justify-between gap-4">
          <DateFieldSkeleton />
          <DateFieldSkeleton alignEnd />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-4 py-3">
      {forecastedStart && forecastedFinish && (
        <TooltipScheduleBar
          forecastedStart={forecastedStart}
          forecastedFinish={forecastedFinish}
          latestFinish={latestFinish}
          isAtRisk={isAtRisk}
        />
      )}
      <div className="flex justify-between gap-4">
        <div className="flex flex-col">
          <span>{t(($) => $.loopVisualization.timelineTooltip.forecastedStart)}:</span>
          <span>{formatDate(forecastedStart, fallback)}</span>
        </div>
        <div className="flex flex-col items-end">
          <span>{t(($) => $.loopVisualization.timelineTooltip.forecastedFinish)}:</span>
          <span>{formatDate(forecastedFinish, fallback)}</span>
        </div>
      </div>
      {latestFinish && (
        <div className="flex flex-col items-end">
          <span>{t(($) => $.loopVisualization.timelineTooltip.latestFinish)}:</span>
          <span className="font-bold">{formatDate(latestFinish, fallback)}</span>
        </div>
      )}
      {deadline && (
        <>
          <div className="border-b border-border-neutral" />
          <div className="flex items-end justify-between gap-4">
            <div className="flex flex-col">
              <span>{t(($) => $.loopVisualization.timelineTooltip.deadline)}:</span>
              <span>{formatDate(deadline, fallback)}</span>
            </div>
            {isAtRisk && (
              <Badge variant="outline" className="shrink-0 gap-1 text-text-error">
                <Warning className="size-3.5" />
                {t(($) => $.loopVisualization.timelineTooltip.atRisk)}
              </Badge>
            )}
          </div>
        </>
      )}
    </div>
  );
};
